import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  CarsCompletedThroughputData,
  CarsThroughputPeriodGroupBy,
  Professional,
} from "../../../types";
import { adminApi, authService, professionalsApi } from "../../../services";
import {
  formatDateShort,
  formatMonthYearLong,
  getBrazilCalendarMonthBounds,
  getTodayMonthYearBrazil,
} from "../../../utils/dateUtils";
import { useCompany } from "../../../contexts/CompanyContext";
import { AdminPageHeader } from "../shared";
import { Card } from "../../shared";
import { Icons } from "../../shared/Icons";
import { CarsCompletedThroughputPanel } from "./CarsCompletedThroughputPanel";
import { CarsCompletedProgressSummary } from "./CarsCompletedProgressSummary";
import { CarsThroughputFiltersCard } from "./CarsThroughputFiltersCard";

export const AdminCarsCompletedThroughputPage: React.FC = () => {
  const navigate = useNavigate();
  const { company } = useCompany();
  const userData = authService.getUserData();
  const isProfessional = userData?.userType === "professional";
  const loggedInProfessionalId = isProfessional ? userData?.id : undefined;

  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState<
    number | null
  >(null);
  const [loadingProfessionals, setLoadingProfessionals] = useState(false);

  const [monthYear, setMonthYear] = useState(() => getTodayMonthYearBrazil());
  const [data, setData] = useState<CarsCompletedThroughputData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isCarCompany = company?.category === "car";
  const { month: selectedMonth, year: selectedYear } = monthYear;

  useEffect(() => {
    if (!isCarCompany || !company?.id) return;

    const loadProfessionals = async () => {
      setLoadingProfessionals(true);
      try {
        const response = await professionalsApi.getProfessionalsByCompany();
        if (response.success && response.data) {
          const active = response.data.filter((p) => p.isActive !== false);

          if (isProfessional && loggedInProfessionalId) {
            const self = active.find((p) => p.id === loggedInProfessionalId);
            setProfessionals(self ? [self] : []);
          } else {
            setProfessionals(active);
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingProfessionals(false);
      }
    };

    loadProfessionals();
  }, [isCarCompany, isProfessional, company?.id, loggedInProfessionalId]);

  const professionalIdForApi =
    loggedInProfessionalId ?? selectedProfessionalId ?? undefined;

  const monthBounds = useMemo(
    () => getBrazilCalendarMonthBounds(selectedYear, selectedMonth),
    [selectedMonth, selectedYear],
  );

  const loadData = useCallback(async () => {
    if (!isCarCompany) return;

    setLoading(true);
    setError(null);

    try {
      const res = await adminApi.getCarsCompletedThroughput({
        startDate: monthBounds.startDate,
        endDate: monthBounds.endDate,
        professionalId: professionalIdForApi,
      });
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setData(null);
        setError(res.error || "Erro ao carregar produção de veículos");
      }
    } catch (e) {
      setData(null);
      setError(
        e instanceof Error
          ? e.message
          : "Erro ao carregar produção de veículos",
      );
    } finally {
      setLoading(false);
    }
  }, [isCarCompany, professionalIdForApi, monthBounds.endDate, monthBounds.startDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const currentMonthYear = getTodayMonthYearBrazil();
  const isSelectedCurrentMonth =
    selectedMonth === currentMonthYear.month &&
    selectedYear === currentMonthYear.year;

  const monthLabel = isSelectedCurrentMonth
    ? "Este mês"
    : formatMonthYearLong(selectedMonth, selectedYear);

  const rangeLabel = `${formatDateShort(monthBounds.startDate)} — ${formatDateShort(monthBounds.endDate)}`;

  const activeProfessionalCount = isProfessional ? 1 : professionals.length;

  const displayProfessionals = useMemo((): Professional[] => {
    if (professionals.length > 0) return professionals;
    if (isProfessional && loggedInProfessionalId && userData) {
      return [
        {
          id: loggedInProfessionalId,
          companyId: userData.companyId,
          name: userData.name,
          phoneNumber: "",
          createdAt: "",
          updatedAt: "",
        },
      ];
    }
    return [];
  }, [professionals, isProfessional, loggedInProfessionalId, userData]);

  const handlePeriodClick = useCallback(
    (period: string, periodKind: CarsThroughputPeriodGroupBy) => {
      const params = new URLSearchParams({
        groupBy: periodKind,
        period,
      });
      if (professionalIdForApi != null) {
        params.set("professionalId", String(professionalIdForApi));
      }
      navigate(`/admin/cars-throughput/period?${params.toString()}`);
    },
    [navigate, professionalIdForApi],
  );

  if (!isCarCompany) {
    return (
      <div className="space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in">
        <AdminPageHeader
          icon={<Icons.Car sx={{ fontSize: 28 }} />}
          title="Produção de Veículos"
          subtitle="Relatório de veículos concluídos por esforço."
        />
        <Card className="p-10 sm:p-14 border border-gray-100 shadow-sm">
          <div className="flex flex-col items-center text-center max-w-md mx-auto">
            <Icons.Car sx={{ fontSize: 56 }} className="text-gray-300" />
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Indisponível para este segmento
            </h2>
            <p className="mt-2 text-gray-600 text-sm sm:text-base leading-relaxed">
              Este relatório está disponível apenas para empresas do segmento
              automotivo.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const filtersProfessionalId = isProfessional
    ? loggedInProfessionalId ?? null
    : selectedProfessionalId;

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<Icons.Car sx={{ fontSize: 28 }} />}
        title="Produção de Veículos"
        subtitle="Veículos concluídos por esforço (leve, médio e pesado), agrupados por dia, semana e mês."
      />

      <CarsThroughputFiltersCard
        month={selectedMonth}
        year={selectedYear}
        onMonthYearChange={(m, y) => setMonthYear({ month: m, year: y })}
        disabled={loading}
        professionals={displayProfessionals}
        selectedProfessionalId={filtersProfessionalId}
        onSelectProfessional={
          isProfessional ? undefined : setSelectedProfessionalId
        }
        loadingProfessionals={loadingProfessionals}
        professionalView={isProfessional}
      />

      {data && (
        <CarsCompletedProgressSummary
          progressSpan={data.progressSpan}
          totalCompletedWithCar={data.totalCompletedWithCar}
          rangeLabel={`${formatDateShort(monthBounds.startDate)} — ${formatDateShort(monthBounds.endDate)} · ${formatMonthYearLong(selectedMonth, selectedYear)}`}
          activeProfessionalCount={activeProfessionalCount}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <CarsCompletedThroughputPanel
          title={`${monthLabel} — por dia`}
          subtitle="Por dia"
          rangeLabel={rangeLabel}
          rows={data?.byDay ?? []}
          periodKind="day"
          loading={loading}
          error={error}
          onRetry={loadData}
          onPeriodClick={handlePeriodClick}
        />
        <CarsCompletedThroughputPanel
          title={`${monthLabel} — por semana`}
          subtitle="Por semana"
          rangeLabel={rangeLabel}
          rows={data?.byWeek ?? []}
          periodKind="week"
          loading={loading}
          error={error}
          onRetry={loadData}
          onPeriodClick={handlePeriodClick}
        />
        <CarsCompletedThroughputPanel
          title={`${monthLabel} — por mês`}
          subtitle="Por mês"
          rangeLabel={rangeLabel}
          rows={data?.byMonth ?? []}
          periodKind="month"
          loading={loading}
          error={error}
          onRetry={loadData}
          onPeriodClick={handlePeriodClick}
        />
      </div>
    </div>
  );
};
